import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import RoleApi from "../api/manageRoleApi";
import { toast } from "react-toastify";
import { setFetchRoleData } from "../../../../core/data/redux/authSlice";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import { handleModalAction } from "../../../../utils/helpers/handleModalAction";

const FunctionalityAccessModal = ({ role }) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  // Use useRef to store previous functionalityAccess values
  const prevFunctionalityAccessRef = useRef(role.functionalityAccess || {});

  const mutationUpdateRole = useMutation({
    mutationFn: RoleApi.updateRole,
    onSuccess: () => {
      queryClient.invalidateQueries(["roles"]);
      dispatch(setFetchRoleData());
      toast.success("Role updated successfully.");
      handleModalAction("functionality_access", "hide");
    },
    onError: (error) => {
      toast.error(error.response?.data?.msg || "Failed to update role.");
    },
  });

  // Formik setup
  const formik = useFormik({
    initialValues: {
      functionalityAccess: role.functionalityAccess || {},
    },
    enableReinitialize: true, // Ensures values update when role changes
    onSubmit: (values) => {
      const prevValues = prevFunctionalityAccessRef.current;

      // Compare values, if unchanged, show warning
      if (
        JSON.stringify(values.functionalityAccess) ===
        JSON.stringify(prevValues)
      ) {
        toast.warning("No changes detected.");
        return;
      }

      mutationUpdateRole.mutate({
        id: role._id,
        updatedRoleData: { functionalityAccess: values.functionalityAccess },
      });
    },
  });

  // Update ref when role changes
  useEffect(() => {
    if (role) {
      prevFunctionalityAccessRef.current = role.functionalityAccess || {};
    }
  }, [role]);

  return (
    <div className="modal fade" id="functionality_access" role="dialog">
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Functionality Access</h5>
            <Link to="#" className="btn btn-light me-2" data-bs-dismiss="modal">
              <i className="ti ti-x" />
            </Link>
          </div>
          <form onSubmit={formik.handleSubmit}>
            <div className="modal-body">
              {Object.keys(formik.values.functionalityAccess)
                .filter((key) => key !== "_id")
                .map((key) => (
                  <div key={key} className="form-check form-switch mb-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={key}
                      checked={formik.values.functionalityAccess[key]}
                      onChange={(e) => {
                        formik.setFieldValue(
                          `functionalityAccess.${key}`,
                          e.target.checked
                        );
                      }}
                    />
                    <label
                      className="form-check-label text-capitalize"
                      htmlFor={key}
                    >
                      {key.replace(/([A-Z])/g, " $1").trim()}
                    </label>
                  </div>
                ))}
            </div>
            <div className="modal-footer">
              <div className="d-flex align-items-center justify-content-end m-0">
                <Link
                  to="#"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </Link>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={mutationUpdateRole.isPending}
                >
                  {mutationUpdateRole.isPending ? "Updating..." : "Update"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FunctionalityAccessModal;
